import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useSearchParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import BossesTable from "../Components/BossesTable";
import Pagination from "../Components/Pagination";
import ApproveModal from "../Components/modals/ApproveModal";
import DeleteModal from "../Components/modals/DeleteModal";
import BossDetailsModal from "../Components/SideModal";
import { GetAllData } from "../NetworkCalls/Products/ServerReq";
import { axiosInstance } from "../axios/index";
import ConfirmRejectModal from "../Components/modals/ConfirmRejectModal";
import ConfirmApproveModal from "../Components/modals/ConfirmApproveModal";

const PageContainer = styled('div')(({ theme }) => ({
  padding: "24px",
  backgroundColor: "#f5f6fe",
  minHeight: "100vh",
  marginLeft: "40px",
  width: "calc(100% - 40px)",
  marginTop: "64px",
  display: "flex",
  flexDirection: "column",
  // alignItems: "center",

  '@media (max-width: 768px)': {
    marginLeft: "0",
    width: "100%",
    padding: "16px",
    marginTop: "56px",
  }
}));

const Title = styled.h1`
  font-family: 'Urbanist';
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 24px;
`;

const PendingRequests = () => {
  const [data, setData] = useState([]);
  const [pageData, setPageData] = useState([]);
  const [loader, setLoader] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [openApprove, setOpenApprove] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedBoss, setSelectedBoss] = useState(null);
  const [openSideModal, setOpenSideModal] = useState(false);
  const [confirmApproveModalOpen, setConfirmApproveModalOpen] = useState(false);
  const [bossToApprove, setBossToApprove] = useState(null);
  const [confirmRejectModalOpen, setConfirmRejectModalOpen] = useState(false);
  const [bossToReject, setBossToReject] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);

  // URL parameters handling
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const renderNameWithIcon = (name, profileUrl) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {name}
      {profileUrl && (
        <a href={profileUrl} target="_blank" rel="noopener noreferrer">
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M5.92962 2.31053H2.2324C1.90555 2.31053 1.59208 2.44037 1.36096 2.67149C1.12984 2.90261 1 3.21608 1 3.54293V9.70496C1 10.0318 1.12984 10.3453 1.36096 10.5764C1.59208 10.8075 1.90555 10.9374 2.2324 10.9374H8.39443C8.72128 10.9374 9.03475 10.8075 9.26587 10.5764C9.49699 10.3453 9.62683 10.0318 9.62683 9.70496V6.00774M5.31342 6.62395L10.8592 1.07812M10.8592 1.07812H7.77823M10.8592 1.07812V4.15914" stroke="#181C23" strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </a>
      )}
    </div>
  );

  const columns = [
    { key: "userName", label: "Recommended By", renderer: (_, item) => renderNameWithIcon(item.userName, item.userProfileUrl) },
    { key: "bossName", label: "Boss Name", renderer: (_, item) => renderNameWithIcon(item.bossName, item.profileUrl) },
    { key: "companyName", label: "Company" },
    { key: "industry", label: "Industry" },
    { key: "action", label: "Recommendation" }
  ];

  const handleReject = async (item) => {
    try {
      setRejectLoading(true);
      const userId = JSON.parse(localStorage.getItem("info"))?.userId;
      const response = await axiosInstance.post(
        `recommendation/reject-recommendation?userId=${userId}`,
        { id: item._id }
      );
      console.log("Rejection Success:", response.data);
      setOpenSideModal(false);
      setConfirmRejectModalOpen(false);
      // Remove the recommendation ID from URL and refresh data
      setSearchParams({});
      window.location.reload();
    } catch (error) {
      console.error("Rejection Failed:", error);
    } finally {
      setRejectLoading(false);
    }
  };

  // Handle URL parameter for specific recommendation
  const handleRecommendationFromURL = (recommendationId, fetchedData) => {
    if (recommendationId && fetchedData.length > 0) {
      const targetRecommendation = fetchedData.find(item => item._id === recommendationId);
      if (targetRecommendation) {
        setSelectedBoss(targetRecommendation);
        setOpenSideModal(true);
      } else {
        console.warn(`Recommendation with ID ${recommendationId} not found`);
        // Remove invalid ID from URL
        setSearchParams({});
      }
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userId = JSON.parse(localStorage.getItem("info"))?.userId;
        const response = await GetAllData(`recommendation/allPendingRecommendations?userId=${userId}`);
        setData(response);
        setFilteredData(response);
        
        // Check for recommendation ID in URL
        const recommendationId = searchParams.get('recommendationId');
        if (recommendationId) {
          handleRecommendationFromURL(recommendationId, response);
        }
        
        setLoader(false);
      } catch (error) {
        console.error("Error fetching pending recommendations:", error);
        setLoader(false);
      }
    };
    fetchData();
  }, [searchParams]);

  const handleApprove = async (item) => {
    try {
      setApproveLoading(true);
      const userId = JSON.parse(localStorage.getItem("info"))?.userId;
      const response = await axiosInstance.put(
        `recommendation/approveRecommendation?userId=${userId}`,
        {
          id: item._id,
        }
      );
      console.log("Approval Success:", response.data);
      setOpenApprove(false);
      setOpenSideModal(false);
      // Remove the recommendation ID from URL and refresh data
      setSearchParams({});
      window.location.reload();
    } catch (error) {
      console.error("Approval Failed:", error);
    } finally {
      setApproveLoading(false);
    }
  };

  const handleDelete = async () => {
    console.log("Deleting", selectedItem);
    // API call logic here
    setOpenDelete(false);
  };

  const handleViewDetails = (item) => {
    setSelectedBoss(item);
    setOpenSideModal(true);
    // Update URL with recommendation ID
    setSearchParams({ recommendationId: item._id });
  };

  const handleCloseSideModal = () => {
    setOpenSideModal(false);
    setSelectedBoss(null);
    // Remove recommendation ID from URL
    setSearchParams({});
  };

  if (loader) {
    return (
      <PageContainer>
        <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
          <CircularProgress color="inherit" sx={{ color: "#12188A" }} />
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Title>Pending Requests</Title>

      {data.length === 0 ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="200px">
          <Typography style={{ color: '#64748B', fontSize: '14px' }}>No Pending Recommendations</Typography>
        </Box>
      ) : (
        <>
          <BossesTable
            data={pageData}
            columns={columns}
            onActionClick={handleViewDetails}
            actionLabel="View"
            showAvatar={false}
          />
          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Pagination data={filteredData} setPageData={setPageData} itemsPerPage={10} />
          </Box>
        </>
      )}

      <DeleteModal
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        onDelete={handleDelete}
        isDeleting={deleteLoading}
      />
      
      {selectedBoss && (
        <BossDetailsModal
          open={openSideModal}
          onClose={handleCloseSideModal}
          bossDetails={selectedBoss}
          viewType="pending-request"
          onApprove={() => {
            setBossToApprove(selectedBoss);
            setConfirmApproveModalOpen(true);
          }}
          onReject={() => {
            setBossToReject(selectedBoss);
            setConfirmRejectModalOpen(true);
          }}
        />
      )}
      
      <ConfirmApproveModal
        open={confirmApproveModalOpen}
        onClose={() => setConfirmApproveModalOpen(false)}
        onConfirm={() => {
          handleApprove(bossToApprove);
        }}
        loader={approveLoading}
      />

      <ConfirmRejectModal
        open={confirmRejectModalOpen}
        onClose={() => setConfirmRejectModalOpen(false)}
        onConfirm={() => handleReject(bossToReject)}
        loader={rejectLoading}
      />
    </PageContainer>
  );
};

export default PendingRequests;